import type { Command, TimedCommand } from './commands.js';
import type { LevelDef } from './level.js';
import { onStandardLift } from './level.js';
import { createInitialState } from './state.js';
import { step } from './tick.js';
import type { GameState, InteractionMode } from './types.js';
import type { Loadout } from './upgrades.js';
import { BASE_LOADOUT } from './upgrades.js';

/**
 * A whole play session as ~1 KB of data.
 *
 * Because `step` is deterministic, `{ levelId, mode, seed, commands }` fully
 * reconstructs a run. That makes every playtest a regression fixture: record
 * once, replay in CI forever, and any rule change that alters the outcome shows
 * up as a hash mismatch instead of a silent behaviour drift.
 */
export const REPLAY_FORMAT_VERSION = 1;

export interface ReplayExpectation {
  readonly tick: number;
  readonly status: string;
  readonly delivered: number;
  readonly elapsedMs: number;
  readonly stateHash: string;
}

export interface ReplayLog {
  readonly version: typeof REPLAY_FORMAT_VERSION;
  readonly levelId: string;
  readonly mode: InteractionMode;
  readonly seed: number;
  /**
   * Hardware installed for this run. Absent means the base lift, which is what
   * every session recorded before v0.3 was played on — so those keep reproducing
   * without a format bump.
   */
  readonly loadout?: Loadout;
  /**
   * Which lift `loadout` is read in (ADR-0027): the building's id when the
   * level's building has a lift of its own. Absent means the standard lift —
   * which is what every session recorded before ADR-0027 was played on, in any
   * building, so those replay on it and keep reproducing.
   */
  readonly lift?: string;
  /** Free-form note: who played, on what build, why this run was kept. */
  readonly label?: string;
  readonly commands: readonly TimedCommand[];
  /** Written when the fixture is captured; checked by `pnpm verify:replays`. */
  readonly expected?: ReplayExpectation;
  /**
   * Set when the run was left before it ended (v0.8): through the pause menu
   * (`quit`, `restart`) or by the page closing (`closed`). Its `expected` is
   * the moment it was left, and `replay` stops there instead of playing the
   * level out — a run nobody finished has no ending to reproduce. Absent on
   * every run that reached the results screen, and on every log before v0.8.
   */
  readonly endedBy?: 'quit' | 'restart' | 'closed';
  /**
   * The sitting this run was part of (v0.8, ADR-0024): a random id made when
   * the game was opened and held only in memory, so it links the runs of one
   * sitting and nothing else — not two sittings, not a device, not a person.
   * Presentation-side metadata: `replay` and `hashState` never read it.
   */
  readonly sitting?: Sitting;
  /**
   * Set when the save this run was played from had used Developer tools
   * (*Unlock every level*, *Add coins*) since it was last reset (v0.8). A run
   * from such a save is not a newcomer's, whoever played it. It says nothing
   * about who that was. `replay` and `hashState` never read it.
   */
  readonly devTools?: true;
}

export interface Sitting {
  /** Random, made at launch, never stored on the device. */
  readonly id: string;
  /** This run's place in the sitting: 1 for the first run after opening the game. */
  readonly run: number;
  /** Milliseconds from opening the game to this run ending. */
  readonly atMs: number;
}

/**
 * FNV-1a over a canonical projection of the state. Covers everything a rule
 * change could plausibly move; deliberately excludes derived render-only values.
 */
export function hashState(state: GameState): string {
  const { elevator, score } = state;
  const parts: (string | number)[] = [
    state.tick,
    state.elapsedMs,
    state.status,
    state.endReason ?? '-',
    elevator.positionMilli,
    elevator.phase,
    elevator.doorPhase,
    elevator.targetFloor ?? -1,
    elevator.phaseTimerMs,
    elevator.dwellMs,
    elevator.load,
    elevator.riders.join(','),
    elevator.boardQueue.join(','),
    // Stops in the player's order: only a run that has some adds to the hash.
    ...(elevator.stops.length > 0 ? [`stops ${elevator.stops.join('.')}`] : []),
    // The rest of the bank (ADR-0034). Only a level with more than one car adds
    // to the hash, so every replay recorded with one hashes as it did.
    ...state.extraCars.flatMap((car) => [
      car.positionMilli,
      car.phase,
      car.doorPhase,
      car.targetFloor ?? -1,
      car.phaseTimerMs,
      car.dwellMs,
      car.load,
      car.riders.join(','),
      car.boardQueue.join(','),
      ...(car.stops.length > 0 ? [`stops ${car.stops.join('.')}`] : []),
    ]),
    score.delivered,
    score.totalWaitMs,
    score.totalRideMs,
    score.distanceMilli,
    score.doorCycles,
    score.abandoned,
    score.couriersOnTime,
    score.couriersLate,
    score.vipsServed,
    Math.round(score.service),
  ];
  for (const p of state.passengers) {
    parts.push(p.id, p.state, p.boardedMs ?? -1, p.deliveredMs ?? -1, p.abandonedMs ?? -1);
    // A round still to make (ADR-0031). Only someone who has one adds to the
    // hash, so every replay recorded before rounds existed hashes as it did.
    if (p.errands.length > 0) parts.push(p.errands.join('.'));
  }

  let hash = 0x811c9dc5;
  const text = parts.join('|');
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

/** Group a flat command log into a per-tick lookup. */
function indexByTick(commands: readonly TimedCommand[]): Map<number, Command[]> {
  const byTick = new Map<number, Command[]>();
  for (const entry of commands) {
    const bucket = byTick.get(entry.tick);
    if (bucket) bucket.push(entry.command);
    else byTick.set(entry.tick, [entry.command]);
  }
  return byTick;
}

export interface ReplayResult {
  readonly state: GameState;
  readonly hash: string;
}

/**
 * Re-run a recorded session against the current rules.
 *
 * Runs past the last command so the level still resolves — a player who routes
 * the car and then stops tapping should still see the run end. A run that was
 * left early (`endedBy`) stops at the tick it was left instead.
 */
export function replay(log: ReplayLog, level: LevelDef, maxTicks = 60_000): ReplayResult {
  if (log.levelId !== level.id) {
    throw new Error(`Replay is for level "${log.levelId}" but "${level.id}" was supplied`);
  }
  if (log.version !== REPLAY_FORMAT_VERSION) {
    throw new Error(`Unsupported replay format version ${log.version}`);
  }

  // The lift it was played on, which before ADR-0027 was always the standard one.
  if (log.lift !== undefined && log.lift !== level.lift?.id) {
    throw new Error(
      `Replay was played on the "${log.lift}" lift but ${level.id} has "${level.lift?.id ?? 'standard'}"`,
    );
  }
  const played = log.lift === undefined ? onStandardLift(level) : level;

  const state = createInitialState(played, {
    mode: log.mode,
    seed: log.seed,
    loadout: log.loadout ?? BASE_LOADOUT,
  });
  const byTick = indexByTick(log.commands);
  const lastCommandTick = log.commands.reduce((max, c) => Math.max(max, c.tick), 0);
  // A run left early stops where it was left; everything else plays out.
  const leftAt = log.endedBy !== undefined && log.expected ? log.expected.tick : maxTicks;
  const limit = Math.min(maxTicks, lastCommandTick + 60_000, leftAt);

  while (state.status === 'playing' && state.tick < limit) {
    step(state, played, byTick.get(state.tick) ?? []);
  }

  return { state, hash: hashState(state) };
}

/** Accumulates a command log while someone plays. The game layer owns one of these. */
export class ReplayRecorder {
  private readonly commands: TimedCommand[] = [];

  constructor(
    private readonly levelId: string,
    private readonly mode: InteractionMode,
    private readonly seed: number,
    private readonly loadout: Loadout = BASE_LOADOUT,
    /** The building's id when it has a lift of its own; see `ReplayLog.lift`. */
    private readonly lift?: string,
  ) {}

  record(tick: number, command: Command): void {
    this.commands.push({ tick, command });
  }

  get length(): number {
    return this.commands.length;
  }

  /** Snapshot the session, stamping the outcome so CI can assert on it later. */
  finish(state: GameState, label?: string): ReplayLog {
    return {
      version: REPLAY_FORMAT_VERSION,
      levelId: this.levelId,
      mode: this.mode,
      seed: this.seed,
      loadout: this.loadout,
      ...(this.lift === undefined ? {} : { lift: this.lift }),
      ...(label === undefined ? {} : { label }),
      commands: [...this.commands],
      expected: {
        tick: state.tick,
        status: state.status,
        delivered: state.score.delivered,
        elapsedMs: state.elapsedMs,
        stateHash: hashState(state),
      },
    };
  }
}
